import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  AssigneeCell as AssigneeTableCell,
  Avatar,
  CellLayout,
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  FAIconName,
  FireflyIconButton,
  MultiSelectOption,
  Popover,
  ProviderAvatarBlock,
  Tag,
  TagColor,
  Text,
  UniversalSelect,
} from '@equip.health/ui';
import { debounce } from 'lodash';

import {
  CareTeamProviderType,
  CareTeamProviderTypeNames,
} from '~/lib/constants/patients.constants';
import { getDisplayNameOrLegalNameForProvider } from '~/lib/util/user.util';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';
import { useProvidersSearchData } from '../../InquiryDetail/useProvidersSearchData';
import { useDatadog } from '~/lib/hooks/useDatadog';

const AssignmentCell = ({
  doesCurrentUserMatchRole,
  firstAssignee,
  handleAddAssignment,
  handleRemoveAssignment,
  inquiryId,
  onSupervisorAdd,
  providerRole,
  version,
}: {
  doesCurrentUserMatchRole: boolean;
  firstAssignee?: InquiryListItemAssignment;
  handleAddAssignment?: (
    inquiryId: string,
    providerRole: CareTeamProviderType,
    version: string,
  ) => void;
  handleRemoveAssignment?: (
    firstAssignee: InquiryListItemAssignment,
    inquiryId: string,
    providerRole: CareTeamProviderType,
    version: string,
  ) => void;
  inquiryId: string;
  /**
   * Callback invoked when an admissions supervisor assigns a provider.
   * Only shown to users with Admissions Supervisor role.
   * @param provider The selected provider to assign
   * @param currentAssignee The currently assigned provider (if editing an existing assignment)
   */
  onSupervisorAdd?: (
    provider: GetProviderByIdResponse,
    currentAssignee?: InquiryListItemAssignment,
  ) => void;
  providerRole: CareTeamProviderType;
  version: string;
}) => {
  const { logError } = useDatadog();
  const isSupervisorMode = !!onSupervisorAdd;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const {
    dataList,
    error: apiError,
    handleSearch,
    isLoading,
    setSearchInput,
  } = useProvidersSearchData({});

  const roleFilter = CareTeamProviderTypeNames[providerRole];
  const debouncedSearchRef = useRef<ReturnType<typeof debounce>>();

  const providerRoleIdSegment = useMemo(
    () =>
      String(providerRole)
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9\-_:.]/g, '-')
        .replace(/-+/g, '-'),
    [providerRole],
  );

  const popoverContentId = useMemo(
    () => `supervisor-assign-content__${inquiryId}__${providerRoleIdSegment}`,
    [inquiryId, providerRoleIdSegment],
  );

  useEffect(() => {
    if (!isSupervisorMode || roleFilter) {
      return;
    }

    logError(
      new Error(`No role filter found for provider role: ${providerRole}`),
      {
        providerRole,
        inquiryId,
      },
    );
  }, [inquiryId, isSupervisorMode, logError, providerRole, roleFilter]);

  useEffect(() => {
    debouncedSearchRef.current = debounce((term: string) => {
      if (term && roleFilter) {
        setSearchError(null);
        handleSearch(term, 1, roleFilter);
      }
    }, 300);

    return () => {
      debouncedSearchRef.current?.cancel();
    };
  }, [handleSearch, roleFilter]);

  // Auto-focus the input when popover opens
  useEffect(() => {
    if (isPopoverOpen && selectRef.current) {
      // Use setTimeout to ensure popover content is rendered
      setTimeout(() => {
        const input = selectRef.current?.querySelector('input');
        input?.focus();
      }, 0);
    }
  }, [isPopoverOpen]);

  const handleAdd = useCallback(() => {
    if (isSupervisorMode) {
      setIsPopoverOpen(true);
    } else {
      handleAddAssignment?.(inquiryId, providerRole, version);
    }
  }, [handleAddAssignment, inquiryId, isSupervisorMode, providerRole, version]);

  const handleRemove = useCallback(
    () =>
      firstAssignee &&
      handleRemoveAssignment?.(firstAssignee, inquiryId, providerRole, version),
    [firstAssignee, handleRemoveAssignment, inquiryId, providerRole, version],
  );

  const selectRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    debouncedSearchRef.current?.cancel();
    setIsPopoverOpen(false);
    setSearchInput('');
    setSearchError(null);
    // Reset scroll position when popover closes
    setTimeout(() => {
      const menuList = selectRef.current?.querySelector('[class*="MenuList"]');
      if (menuList) {
        menuList.scrollTop = 0;
      }
    }, 0);
  };

  const handleProviderSelect = (
    provider: GetProviderByIdResponse | undefined,
  ) => {
    if (!provider) {
      logError(new Error('Provider not found in search results'), {
        inquiryId,
        providerRole,
      });
      setSearchError('Provider not found. Please try searching again.');
      return;
    }

    // Skip API call if selecting the same user already assigned
    if (firstAssignee && provider.userId === firstAssignee.externalId) {
      handleClose();
      return;
    }

    onSupervisorAdd?.(provider, firstAssignee);
    handleClose();
  };

  const assigneeLegalNameOrDisplayName = getDisplayNameOrLegalNameForProvider(
    firstAssignee?.displayFirstName,
    firstAssignee?.firstName,
    firstAssignee?.displayLastName,
    firstAssignee?.lastName,
  );

  const providerOptions = useMemo<MultiSelectOption[]>(
    () =>
      dataList.map((provider) => {
        const hasDisplayName =
          provider.displayFirstName && provider.displayLastName;
        const displayName = getDisplayNameOrLegalNameForProvider(
          provider.displayFirstName,
          provider.firstName,
          provider.displayLastName,
          provider.lastName,
        );
        return {
          label: (
            <ProviderAvatarBlock
              avatarUrl={provider.avatar?.thumbnailUrl}
              displayName={
                hasDisplayName
                  ? {
                      firstName: provider.displayFirstName,
                      lastName: provider.displayLastName,
                    }
                  : undefined
              }
              id={`provider-option__${provider.userId}`}
              legalName={{
                firstName: provider.firstName,
                lastName: provider.lastName,
              }}
              pronouns={provider.pronouns}
              shouldShowPronouns={provider.pronouns?.length > 0}
              variant="compact"
            />
          ),
          searchData: displayName.toLowerCase(),
          value: provider.userId,
        };
      }),
    [dataList],
  );

  const popoverBody = isSupervisorMode ? (
    <ChakraBox
      id={popoverContentId}
      overflow="visible"
      padding="22px 24px 24px"
      position="relative"
      ref={selectRef}
      sx={{
        '[class*="control"] + *': {
          position: 'relative !important',
        },
        '[class*="control"] + *>div': {
          border: 'none',
          boxShadow: 'none',
        },
        '[class*="control"] + * .option': {
          padding: '12px 6px',
        }
      }}
    >
      <UniversalSelect
        aria-describedby={`assignment-instructions-${inquiryId}-${providerRoleIdSegment}`}
        aria-label="Assign team member"
        id={`supervisor-search__${inquiryId}__${providerRoleIdSegment}`}
        isLoading={isLoading}
        isSearchable
        isSingleSelect
        menuIsOpen={isPopoverOpen}
        onChange={(selected: MultiSelectOption | null) => {
          if (!selected) return;
          const provider = dataList.find((p) => p.userId === selected.value);
          handleProviderSelect(provider);
        }}
        onInputChange={(value) => {
          debouncedSearchRef.current?.(value);
        }}
        onMenuClose={() => {}}
        options={providerOptions}
        placeholder={`Search ${roleFilter?.toLowerCase() ?? 'team member'}s`}
      />
      {(searchError || apiError) && (
        <ChakraBox color="red.500" fontSize="sm" mt={2}>
          {searchError || apiError}
        </ChakraBox>
      )}
      <span
        id={`assignment-instructions-${inquiryId}-${providerRoleIdSegment}`}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        Type to search, use arrow keys to navigate, Enter to select
      </span>
    </ChakraBox>
  ) : undefined;

  // Custom trigger for supervisor mode with existing assignment
  const renderSupervisorTriggerWithAssignment = () => (
    <ChakraFlex
      alignItems="center"
      cursor="pointer"
      gap="12px"
      height="60px"
      justifyContent="space-between"
      onClick={handleAdd}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleAdd();
        }
      }}
      paddingLeft="12px"
      paddingRight="16px"
      role="cell"
      tabIndex={0}
      width="100%"
    >
      <ProviderAvatarTooltipWrapper
        isRoleAtAction
        providerExternalId={firstAssignee?.externalId}
      >
        <Avatar
          name={assigneeLegalNameOrDisplayName}
          size="sm"
          src={firstAssignee?.avatar?.thumbnailUrl}
          userType="provider"
        />
      </ProviderAvatarTooltipWrapper>
      <Text noOfLines={1} size="sm">
        {assigneeLegalNameOrDisplayName}
      </Text>
      <FireflyIconButton
        ariaLabel="Reassign team member"
        icon={FAIconName.userEditRegular}
        id={`reassign-button-${inquiryId}-${providerRoleIdSegment}`}
        onClick={(e) => {
          e.stopPropagation();
          handleAdd();
        }}
        size="small"
        variant="tertiary"
      />
    </ChakraFlex>
  );

  const TOP_LEVEL_ID = '';

  // Custom trigger for supervisor mode without assignment
  const renderSupervisorTriggerWithoutAssignment = () => (
    <CellLayout id={'assignee-cell'}>
        <ChakraHStack
          gap="10px"
          id={`${TOP_LEVEL_ID}__secondary-section`}
          justifyContent="space-between"
          width="100%"
        >
        <Tag
          color={TagColor.gray}
          id={`${TOP_LEVEL_ID}__unassigned-tag`}
          value="Unassigned"
        />
      <FireflyIconButton
        ariaLabel="Assign team member"
        icon={FAIconName.userEditRegular}
        id={`assign-button-${inquiryId}-${providerRoleIdSegment}`}
        onClick={handleAdd}
        size="small"
        variant="tertiary"
      />
      </ChakraHStack>
    </CellLayout>
  );

  const renderTriggerContent = () => {
    // Supervisor mode with existing assignment: show custom edit trigger
    if (isSupervisorMode && firstAssignee) {
      return renderSupervisorTriggerWithAssignment();
    }

    // Supervisor mode without assignment: show edit pencil icon
    if (isSupervisorMode && !firstAssignee) {
      return renderSupervisorTriggerWithoutAssignment();
    }

    // All other cases: use AssigneeTableCell
    return (
      <AssigneeTableCell
        assignee={
          firstAssignee && {
            name: assigneeLegalNameOrDisplayName,
            userType: 'provider',
            src: firstAssignee?.avatar?.thumbnailUrl,
          }
        }
        id={`user-${firstAssignee?.externalId}__inquiry-${inquiryId}`}
        isEditable={doesCurrentUserMatchRole || isSupervisorMode}
        onAdd={handleAdd}
        onRemove={handleRemove}
        tooltipWrapper={({ children }) => (
          <ProviderAvatarTooltipWrapper
            isRoleAtAction
            providerExternalId={firstAssignee?.externalId}
          >
            {children}
          </ProviderAvatarTooltipWrapper>
        )}
      />
    );
  };

  // Only use Popover wrapper for supervisor mode
  if (isSupervisorMode) {
    return (
      <ChakraBox position="relative" width="100%">
        <Popover
          aria-label="Assign team member"
          bodyContent={popoverBody}
          bodyOverflow="visible"
          id={`supervisor-assign__${inquiryId}__${providerRoleIdSegment}`}
          isOpen={isPopoverOpen}
          onClose={handleClose}
          onOpen={undefined} // Prevent trigger click from toggling
          bodyPadding='0'
          placement="bottom-start"
          size="lg"
          triggerContent={renderTriggerContent()}
        />
      </ChakraBox>
    );
  }

  // Non-supervisor mode: render directly without Popover wrapper
  return renderTriggerContent();
};
export default AssignmentCell;
